import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";

const STATUS_LABEL = { idea: "Idea", en_desarrollo: "En desarrollo", lanzado: "Lanzado" };

const BLOCKS = [
  { title: "Desarrollo de productos con talentos", desc: "Creamos productos junto a influencers y figuras digitales, aprovechando su audiencia, credibilidad y capacidad de conversación." },
  { title: "Productos para marcas", desc: "Diseñamos líneas comerciales, ediciones especiales, lanzamientos, experiencias y colaboraciones entre marcas y talentos." },
  { title: "Impulso multiplataforma", desc: "Activamos los productos en redes sociales, eventos, transmisiones, contenido orgánico, campañas con influencers y formatos audiovisuales." },
  { title: "De idea a comunidad", desc: "No se trata solo de vender un producto. Se trata de construir una historia, una comunidad y una experiencia alrededor de la marca." },
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(setProducts).catch(() => setProducts([])); }, []);

  return (
    <div data-testid="products-page">
      <section className="pt-20 pb-16 lg:pt-32 lg:pb-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6 font-bold">Productos de marca</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.95] mb-8 max-w-5xl">
            Creamos productos con <span className="gradient-text">historia, comunidad</span> y distribución digital.
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-3xl">
            En INFLUCAST no solo comunicamos marcas: también las creamos, las impulsamos y las convertimos en productos con comunidad.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
          {BLOCKS.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-background p-7 lg:p-8"
            >
              <div className="text-5xl font-display font-black text-primary/20 mb-3">0{i + 1}</div>
              <h3 className="font-display font-bold text-lg tracking-tight mb-2">{b.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-12 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter mb-10">Productos en marcha</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                className="border border-border rounded-sm overflow-hidden bg-background hover:border-primary transition-colors group"
                data-testid={`product-card-${p.id}`}
              >
                <Link to={`/productos/${p.id}`} className="block">
                  <div className="aspect-[4/3] bg-secondary overflow-hidden">
                    {p.image && <img src={resolveMediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3 text-[10px] uppercase tracking-widest font-bold">
                      <span className="text-muted-foreground">{p.category}</span>
                      <span className="text-primary">{STATUS_LABEL[p.status] || p.status}</span>
                    </div>
                    <h3 className="font-display font-bold text-xl tracking-tight mb-2">{p.name}</h3>
                    {p.talent_name && <p className="text-xs text-muted-foreground mb-3">Con {p.talent_name}</p>}
                    <p className="text-sm text-muted-foreground leading-relaxed">{p.short_description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
